import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ArrowUpRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { ARTICLES } from "@/data/articles";
import ArticleReader from "./ArticleReader";

const CATEGORIES = ["All", "Toddlers", "Teens", "Routines", "Mindset"];

export default function Blog({ onWorkWithMe }) {
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);
  const filtered = active === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === active);

  return (
    <section
      id="blog-section"
      data-testid="blog-section"
      className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12 bg-[#F3EEE3] border-y border-[#2F3B2E]/10"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <Label>Articles & Parental Guides</Label>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-[#3B352D]">
              Free insights to start applying today
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`blog-filter-${c.toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-200 ${
                  active === c
                    ? "bg-[#2F3B2E] text-white border-[#2F3B2E]"
                    : "bg-transparent text-[#2F3B2E] border-[#2F3B2E]/25 hover:bg-[#2F3B2E] hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((a) => (
              <motion.article
                layout
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                data-testid="blog-card"
                onClick={() => setSelected(a)}
                className="group flex flex-col rounded-3xl bg-white border border-[#2F3B2E]/10 overflow-hidden hover:border-[#2F3B2E]/25 transition-colors duration-300 cursor-pointer"
              >
                <div className="h-44 overflow-hidden">
                  <img src={a.img} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="rounded-full bg-[#E8E2D5] px-2.5 py-1 text-[11px] font-medium text-[#2F3B2E]">{a.category}</span>
                    <span className="flex items-center gap-1 text-xs text-[#6B665C]">
                      <Clock size={13} /> {a.readTime}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-xl font-medium text-[#3B352D] leading-snug">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#6B665C] leading-relaxed flex-1">{a.excerpt}</p>
                  <p className="mt-3 text-xs italic text-[#6B665C]">{a.source}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono-accent text-[11px] uppercase tracking-wider text-[#6B665C]">{a.date}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2F3B2E] group-hover:text-[#A8916B] transition-colors">
                      Read <ArrowUpRight size={16} />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ArticleReader
        article={selected}
        onClose={() => setSelected(null)}
        onWorkWithMe={() => { setSelected(null); onWorkWithMe?.(); }}
      />
    </section>
  );
}
